import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface QuantityStepperProps {
  value: number;
  onChange: (value: number) => void;
  /** Visual weight of the −/+ buttons: cart rows use ghost, dialogs outline. */
  variant?: 'ghost' | 'outline';
}

/**
 * −/+ stepper whose number is itself an input, so a quantity of 100 is typed rather than clicked
 * to. Typing only reaches the draft on commit (Enter or blur), clamped to a positive integer;
 * anything unparseable reverts to the last real value instead of guessing.
 */
export function QuantityStepper({ value, onChange, variant = 'ghost' }: QuantityStepperProps) {
  const { t } = useTranslation('conversations');
  const [text, setText] = useState(String(value));

  // Adopt outside changes (the −/+ buttons, a discarded draft). Safe against active typing because
  // typing never mutates `value` — only a commit does, and commit re-normalizes `text` itself.
  useEffect(() => {
    setText(String(value));
  }, [value]);

  const commit = () => {
    const parsed = Math.floor(Number(text.trim()));
    if (Number.isFinite(parsed) && parsed >= 1) {
      onChange(parsed);
      setText(String(parsed));
    } else {
      setText(String(value));
    }
  };

  return (
    <div className="flex items-center gap-1">
      <Button
        type="button"
        variant={variant}
        size="icon-sm"
        onClick={() => onChange(Math.max(1, value - 1))}
        disabled={value <= 1}
        aria-label={t('cart.editor.decrease')}
      >
        <Minus className="h-3.5 w-3.5" />
      </Button>
      <Input
        type="text"
        inputMode="numeric"
        value={text}
        onChange={(event) => setText(event.target.value)}
        onFocus={(event) => event.currentTarget.select()}
        onBlur={commit}
        onKeyDown={(event) => {
          // Blur commits; committing here as well would fire onChange twice.
          if (event.key === 'Enter') event.currentTarget.blur();
        }}
        aria-label={t('cart.editor.quantity')}
        className="h-8 w-12 px-1 text-center text-sm tabular-nums"
      />
      <Button
        type="button"
        variant={variant}
        size="icon-sm"
        onClick={() => onChange(value + 1)}
        aria-label={t('cart.editor.increase')}
      >
        <Plus className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
